// artifacts-09-archive: תפריט הפעולות בדף היצירה — ליוצר בלבד. "העבר לארכיון" עם אישור; אחרי ארכוב — חזרה לרשימה עם הודעה
// (בארכיון היצירה לא מוצגת בשום מקום; שחזור — בבקשה מאייבי).
// artifacts-rename: "שינוי שם ותיאור" — טופס קטן באותו תפריט; מטא-דאטה בלבד (בלי גרסה), הכתובת והתזמונים לא משתנים.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import { T } from '../lib/strings.ts';
import { Button } from './ui/button.tsx';
import { Input, Label } from './ui/input.tsx';
import { ErrorView } from './Status.tsx';
import { IconArchive, IconMore, IconPencil } from './brand/icons.tsx';

type Mode = 'menu' | 'archive' | 'rename';

const itemCls =
  'flex min-h-11 w-full cursor-pointer items-center gap-2 rounded-sm px-3 text-start text-[15px] hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring';

export function ArtifactActions({ slug, nameHe, descriptionHe = '' }: { slug: string; nameHe: string; descriptionHe?: string }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>('menu');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [name, setName] = useState(nameHe);
  const [desc, setDesc] = useState(descriptionHe);
  const ref = useRef<HTMLDivElement>(null);
  const nav = useNavigate();
  const qc = useQueryClient();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function close() {
    setOpen(false);
    setMode('menu');
    setErr(null);
    setBusy(false);
  }

  function startRename() {
    setName(nameHe);
    setDesc(descriptionHe);
    setErr(null);
    setMode('rename');
  }

  async function archive() {
    setBusy(true);
    setErr(null);
    try {
      await api.archiveArtifact(slug);
      qc.removeQueries({ queryKey: ['artifact', slug] });
      await qc.invalidateQueries({ queryKey: ['artifacts'] });
      nav('/artifacts', { state: { notice: T.artifacts.archived } });
    } catch (e) {
      setErr(e);
      setBusy(false);
    }
  }

  async function rename(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await api.renameArtifact(slug, { nameHe: name.trim(), descriptionHe: desc.trim() });
      // השם נקרא מהשורה בכל מקום: הדף, הרשימה, הפאנל, התזמונים
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['artifact', slug] }),
        qc.invalidateQueries({ queryKey: ['artifacts'] }),
        qc.invalidateQueries({ queryKey: ['triggers', slug] }),
        qc.invalidateQueries({ queryKey: ['panel'] }),
      ]);
      close();
    } catch (e2) {
      setErr(e2);
      setBusy(false);
    }
  }

  const empty = !name.trim();
  return (
    <div ref={ref} className="relative shrink-0">
      <Button variant="outline" size="icon" aria-label={T.artifacts.actions} aria-haspopup="menu" aria-expanded={open} onClick={() => (open ? close() : setOpen(true))} data-testid="artifact-actions">
        <IconMore size={20} />
      </Button>
      {open && (
        <div
          className="absolute end-0 top-full z-30 mt-2 w-80 max-w-[calc(100vw-2rem)] rounded-md border border-border bg-card p-1 shadow-pop"
          role={mode === 'archive' ? 'alertdialog' : mode === 'rename' ? 'dialog' : 'menu'}
          aria-label={mode === 'rename' ? T.artifacts.rename : T.artifacts.actions}
        >
          {mode === 'menu' && (
            <>
              <button type="button" role="menuitem" className={itemCls} onClick={startRename} data-testid="artifact-rename">
                <IconPencil size={18} className="text-muted-foreground" />
                {T.artifacts.rename}
              </button>
              <button type="button" role="menuitem" className={itemCls} onClick={() => setMode('archive')} data-testid="artifact-archive">
                <IconArchive size={18} className="text-muted-foreground" />
                {T.artifacts.archive}
              </button>
            </>
          )}
          {mode === 'archive' && (
            <div className="flex flex-col gap-3 p-3" data-testid="archive-confirm">
              <p className="m-0 text-sm">{T.artifacts.archiveConfirm(nameHe)}</p>
              {err != null && <ErrorView error={err} />}
              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={close} disabled={busy}>
                  {T.artifacts.cancel}
                </Button>
                <Button variant="destructive" size="sm" onClick={() => void archive()} disabled={busy} data-testid="archive-yes">
                  {busy ? T.artifacts.archiving : T.artifacts.archiveYes}
                </Button>
              </div>
            </div>
          )}
          {mode === 'rename' && (
            <form className="flex flex-col gap-3 p-3" onSubmit={(e) => void rename(e)} data-testid="rename-form">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="rename-name">{T.artifacts.renameName}</Label>
                <Input
                  id="rename-name"
                  value={name}
                  maxLength={120}
                  autoFocus
                  aria-invalid={empty}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="rename-name"
                />
                {empty && <span className="text-sm text-error">{T.artifacts.renameEmpty}</span>}
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="rename-desc">{T.artifacts.renameDescription}</Label>
                <textarea
                  id="rename-desc"
                  value={desc}
                  maxLength={2000}
                  rows={3}
                  onChange={(e) => setDesc(e.target.value)}
                  className="w-full resize-y rounded-sm border border-input/70 bg-card px-3 py-2 text-base text-foreground focus-visible:border-ring focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/25"
                  data-testid="rename-desc"
                />
              </div>
              <p className="m-0 text-sm text-muted-foreground">{T.artifacts.renameHint}</p>
              {err != null && <ErrorView error={err} />}
              <div className="flex flex-wrap justify-end gap-2">
                <Button type="button" variant="ghost" size="sm" onClick={close} disabled={busy}>
                  {T.artifacts.cancel}
                </Button>
                <Button type="submit" size="sm" disabled={busy || empty} data-testid="rename-save">
                  {busy ? T.artifacts.renaming : T.artifacts.renameSave}
                </Button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
